import React from "react";

/**
 * A row of chips that refuses to grow taller than a table row can afford.
 *
 * Feature and model lists are open-ended — the Trinetra catalog alone has
 * twelve entries — and rendering all of them in a narrow table column stacks
 * them vertically, turning one record into a 300px-tall block and pushing
 * everything else off screen. So only the first few are drawn and the rest
 * collapse into a "+N more" chip that carries the full list in its tooltip.
 *
 * @param {string[]} items
 * @param {number}   max      How many to show before collapsing
 * @param {Set}      muted    Items to de-emphasise, e.g. custom (uncatalogued) keys
 * @param {object}   palette  {bg, text, border} tokens for the normal chips
 */
const ChipList = ({ items = [], max = 4, muted, palette, mutedTitle = "Custom" }) => {
  if (items.length === 0) {
    return <span style={{ color: "var(--main-text-muted)" }}>—</span>;
  }

  const shown = items.slice(0, max);
  const hidden = items.slice(max);

  const chipStyle = (item) =>
    muted?.has(item)
      ? {
          background: "transparent",
          color: "var(--main-text-muted)",
          borderColor: "var(--card-border)",
        }
      : {
          background: palette?.bg,
          color: palette?.text,
          borderColor: palette?.border,
        };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {shown.map((item) => (
        <span
          key={item}
          title={muted?.has(item) ? `${mutedTitle}: ${item}` : item}
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold border whitespace-nowrap"
          style={chipStyle(item)}
        >
          {item}
        </span>
      ))}

      {hidden.length > 0 && (
        <span
          // The tooltip is the only place the hidden entries remain readable,
          // so it lists them in full rather than just repeating the count.
          title={hidden.join(", ")}
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold border whitespace-nowrap cursor-help"
          style={{
            background: "transparent",
            color: "var(--main-text-muted)",
            borderColor: "var(--card-border)",
          }}
        >
          +{hidden.length} more
        </span>
      )}
    </div>
  );
};

export default ChipList;
